import { useState, useRef } from "react";
import axios from "axios";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import SponsorsEditor from "@/components/SponsorsEditor";

export const EVENT_TYPES = ["Men's Doubles", "Women's Doubles", "Mixed Doubles", "Coed Doubles", "Men's Singles", "Women's Singles",
  "Boys Doubles", "Girls Doubles", "Junior Mixed Doubles", "Boys Singles", "Girls Singles"];
export const JUNIOR_EVENTS = new Set(["Boys Doubles", "Girls Doubles", "Junior Mixed Doubles", "Boys Singles", "Girls Singles"]);
export const LEVEL_OPTIONS = ["Beginner", "Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Contender", "Future Pro", "Pro", "G.O.A.T.", "All"];
export const TIER_OPTIONS = ["Local", "Regional", "District", "State", "National", "Moneyball"];
export const AGE_OPTIONS = ["8–39", "40–49", "50+", "60+", "70+"];
export const JUNIOR_AGE_OPTIONS = ["U10", "U12", "U14", "U16", "U18"];
const slug = (x) => x.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const FORMAT_LABELS = {
  single_elim: "Single Elimination", double_elim: "Double Elimination",
  round_robin: "Round Robin", pool_play: "Pool Play → Playoffs",
};
const EMPTY = { name: "", format: "single_elim", date: "", end_date: "", days: 1, start_time: "", end_time: "", location: "", zip_code: "", entry_fee: 0, entry_fee_coins: "", additional_event_fee: "", additional_event_fee_coins: "", max_games: 0, description: "", play_style: "no_come_around", playoff_formats: { quarters: 1, semis: 1, finals: 1 }, reg_open_at: "", reg_close_at: "", early_bird_fee: "", early_bird_expires_at: "", vip_addon_fee: "", vip_addon_fee_coins: "", price_display: "card" };
const CHUNK_SIZE = 1.5 * 1024 * 1024;

const DUPR_PSI = [[2.5, 50], [3.0, 55], [3.5, 60], [4.0, 70], [4.5, 80], [5.0, 90]];
export function teamDuprToPsi(combined) {
  const r = Number(combined) / 2;
  if (!r || Number.isNaN(r)) return null;
  let psi = 90;
  if (r <= 2.5) psi = 50;
  else if (r < 5) {
    for (let i = 1; i < DUPR_PSI.length; i++) {
      const [r1, b1] = DUPR_PSI[i - 1], [r2, b2] = DUPR_PSI[i];
      if (r <= r2) { psi = b1 + ((r - r1) / (r2 - r1)) * (b2 - b1); break; }
    }
  }
  return Math.round(psi * 2 * 10) / 10;
}

const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red text-sm";
const lbl = "block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5";

function CheckBox({ checked, onToggle, label, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onToggle} aria-pressed={checked}
      className={`flex items-center gap-2 px-3 py-2 border text-xs font-semibold transition-colors text-left ${
        checked ? "border-pb-red bg-pb-red/10 text-white" : "border-pb-border text-zinc-500 hover:border-zinc-600"}`}>
      <span className={`w-3.5 h-3.5 flex-shrink-0 border flex items-center justify-center ${checked ? "border-pb-red bg-pb-red" : "border-zinc-600"}`}>
        {checked && <span className="text-black text-[9px] font-black leading-none">✓</span>}
      </span>
      {label}
    </button>
  );
}

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function TournamentCreateForm({ onCreated, initial, endpoint }) {
  const [form, setForm] = useState(initial ? {
    name: initial.name, format: initial.format, date: initial.date, end_date: initial.end_date || "", days: initial.days || 1,
    start_time: initial.start_time || "", end_time: initial.end_time || "", location: initial.location,
    zip_code: initial.zip_code || "",
    reg_open_at: isoToLocal(initial.reg_open_at), reg_close_at: isoToLocal(initial.reg_close_at),
    entry_fee: initial.entry_fee || 0, entry_fee_coins: initial.entry_fee_coins || "", max_games: initial.max_games || 0,
    additional_event_fee: initial.additional_event_fee ?? "", additional_event_fee_coins: initial.additional_event_fee_coins ?? "",
    early_bird_fee: initial.early_bird_fee ?? "", early_bird_expires_at: isoToLocal(initial.early_bird_expires_at),
    vip_addon_fee: initial.vip_addon_fee ?? "", vip_addon_fee_coins: initial.vip_addon_fee_coins ?? "",
    price_display: initial.price_display || "card",
    playoff_formats: initial.playoff_formats || { quarters: 1, semis: 1, finals: 1 },
    description: initial.description || "", play_style: initial.play_style || "no_come_around",
    venue: initial.venue || "indoor",
  } : EMPTY);
  const [eventTypes, setEventTypes] = useState(initial?.event_types?.length ? initial.event_types : ["Mixed Doubles"]);
  const [eventCfg, setEventCfg] = useState(() => {
    const base = { levels: initial?.levels?.length ? initial.levels : ["All"], age_brackets: initial?.age_brackets || [] };
    const out = {};
    (initial?.event_types?.length ? initial.event_types : ["Mixed Doubles"]).forEach((et) => {
      const c = initial?.event_config?.[et];
      out[et] = c ? { levels: c.levels || ["All"], age_brackets: c.age_brackets || [] } : { ...base, age_brackets: [...base.age_brackets] };
    });
    return out;
  });
  const [customAges, setCustomAges] = useState({});
  const [tiers, setTiers] = useState(initial?.tiers || []);
  const [prizePools, setPrizePools] = useState(initial?.prize_pools?.length ? initial.prize_pools : []);
  const [teamDuprs, setTeamDuprs] = useState(initial?.team_dupr_caps || {});
  const [partnerRequired, setPartnerRequired] = useState(!!initial?.partner_required);
  const [duprRated, setDuprRated] = useState(!!initial?.dupr_rated);
  const [duprPremium, setDuprPremium] = useState(!!initial?.dupr_premium);
  const [maxEntries, setMaxEntries] = useState(() => {
    const vals = Object.values(initial?.capacities || {}).map(Number).filter(Boolean);
    return vals.length ? Math.max(...vals) : 16;
  });
  const [courts, setCourts] = useState(initial?.courts || []);
  const [sponsors, setSponsors] = useState(initial?.sponsors || []);
  const [image, setImage] = useState(initial?.image || null);
  const [logo, setLogo] = useState(initial?.logo || null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [video, setVideo] = useState(initial?.video || null);
  const [mediaTab, setMediaTab] = useState(initial?.video ? "video" : "image");
  const [uploading, setUploading] = useState(false);
  const [vidProgress, setVidProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);
  const logoFileRef = useRef(null);

  const toggleEvent = (et) => {
    setEventTypes((prev) => {
      if (prev.includes(et)) return prev.filter((x) => x !== et);
      return [...prev, et];
    });
    setEventCfg((prev) => {
      if (prev[et]) return prev;
      const tmpl = Object.entries(prev).find(([k]) => JUNIOR_EVENTS.has(k) === JUNIOR_EVENTS.has(et))?.[1];
      return { ...prev, [et]: tmpl ? { levels: [...tmpl.levels], age_brackets: [...tmpl.age_brackets] } : { levels: ["All"], age_brackets: [] } };
    });
  };
  const cfgOf = (et) => eventCfg[et] || { levels: ["All"], age_brackets: [] };
  const toggleLevel = (et, lv) => setEventCfg((p) => {
    const c = cfgOf(et);
    let next = c.levels.includes(lv) ? c.levels.filter((x) => x !== lv) : lv === "All" ? ["All"] : [...c.levels.filter((x) => x !== "All"), lv];
    return { ...p, [et]: { ...c, levels: next } };
  });
  const toggleAge = (et, a) => setEventCfg((p) => {
    const c = cfgOf(et);
    return { ...p, [et]: { ...c, age_brackets: c.age_brackets.includes(a) ? c.age_brackets.filter((x) => x !== a) : [...c.age_brackets, a] } };
  });
  const addCustomAge = (et) => {
    const a = (customAges[et] || "").trim().slice(0, 24);
    if (!a) return;
    if (!cfgOf(et).age_brackets.includes(a)) toggleAge(et, a);
    setCustomAges({ ...customAges, [et]: "" });
  };
  const copyCfgToAll = (et) => setEventCfg((p) => {
    const src = cfgOf(et);
    const out = { ...p };
    // age groups only copy between events of the same kind (adult ↔ adult, junior ↔ junior); classifications copy everywhere
    eventTypes.forEach((x) => {
      const sameKind = JUNIOR_EVENTS.has(x) === JUNIOR_EVENTS.has(et);
      out[x] = { levels: [...src.levels], age_brackets: sameKind ? [...src.age_brackets] : [...cfgOf(x).age_brackets] };
    });
    return out;
  });
  const unionLevels = () => {
    const all = eventTypes.flatMap((et) => cfgOf(et).levels);
    return all.includes("All") ? ["All"] : LEVEL_OPTIONS.filter((lv) => all.includes(lv));
  };
  const unionAges = () => [...new Set(eventTypes.flatMap((et) => cfgOf(et).age_brackets))];
  const toggleCourt = (c) => setCourts((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c].sort((a, b) => a - b)));

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only images are supported here");
    if (file.size > 8 * 1024 * 1024) return toast.error("Logo is too big — max 8MB");
    setLogoUploading(true);
    try {
      const uploadId = `tlogo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      let res = null;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", total);
        fd.append("filename", file.name);
        fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        res = (await api.post("/community/upload/chunk", fd)).data;
      }
      const url = res.url.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + res.url : res.url;
      setLogo(url);
      toast.success("Tournament logo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLogoUploading(false); }
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only images are supported here");
    if (file.size > 25 * 1024 * 1024) return toast.error("Image is too big — max 25MB");
    setUploading(true);
    try {
      const uploadId = `tourney_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      let res = null;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", total);
        fd.append("filename", file.name);
        fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        res = (await api.post("/community/upload/chunk", fd)).data;
      }
      setImage(res.url);
      setVideo(null);
      toast.success("Tournament image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const getDuration = (file) => new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
    v.onerror = () => { URL.revokeObjectURL(v.src); reject(new Error("bad video")); };
    v.src = URL.createObjectURL(file);
  });

  const pickVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/") && !/\.(mp4|mov|webm|m4v)$/i.test(file.name)) return toast.error("Only videos are supported here — mp4, mov or webm");
    if (file.size > 500 * 1024 * 1024) return toast.error("Video is too big — max 500MB. Trim or compress it and try again.");
    try {
      const dur = await getDuration(file);
      if (dur > 181) return toast.error(`Video is ${Math.round(dur)}s — announcement clips max out at 3 minutes. Trim it down and try again.`);
    } catch {
      // this browser can't decode the codec (e.g., iPhone HEVC) — the server converts it, so let it through
    }
    setUploading(true);
    setVidProgress(0);
    try {
      // signed direct-to-Cloudinary upload — Cloudinary transcodes + serves from its CDN
      const { data: sig } = await api.get("/cloudinary/signature?folder=uploads/tournaments");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);
      const res = await axios.post(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`, fd, {
        onUploadProgress: (e) => setVidProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      const { public_id, format } = res.data;
      const url = format === "mp4"
        ? res.data.secure_url
        : `https://res.cloudinary.com/${sig.cloud_name}/video/upload/q_auto/${public_id}.mp4`;
      setVideo(url);
      setImage(null);
      toast.success("Announcement video uploaded");
    } catch (err) {
      const cldErr = err.response?.data?.error?.message;
      toast.error(cldErr ? `Video upload failed — ${cldErr}` : formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); setVidProgress(0); }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Tournament name is required");
    if (eventTypes.length === 0) return toast.error("Select at least one event type");
    const noLevel = eventTypes.find((et) => cfgOf(et).levels.length === 0);
    if (noLevel) return toast.error(`Pick at least one skill classification for ${noLevel}`);
    if (!form.date) return toast.error("Start date is required");
    if (!form.location.trim()) return toast.error("Location is required");
    if (courts.length === 0) return toast.error("Check the court numbers you'll be using");
    if (form.early_bird_fee !== "" && !form.early_bird_expires_at) return toast.error("Set an expiration date for the Early Bird price");
    setBusy(true);
    try {
      const capacities = {};
      eventTypes.forEach((et) => { capacities[et] = Math.max(2, Math.min(256, Number(maxEntries) || 16)); });
      const payload = {
        name: form.name, format: form.format, event_types: eventTypes, levels: unionLevels(), tiers,
        event_config: Object.fromEntries(eventTypes.map((et) => [et, cfgOf(et)])),
        date: form.date, end_date: form.end_date || null, days: Number(form.days) || 1,
        start_time: form.start_time || null, end_time: form.end_time || null,
        reg_open_at: form.reg_open_at ? new Date(form.reg_open_at).toISOString() : null,
        reg_close_at: form.reg_close_at ? new Date(form.reg_close_at).toISOString() : null,
        location: form.location, entry_fee: Number(form.entry_fee) || 0,
        zip_code: form.zip_code || null,
        partner_required: partnerRequired,
        dupr_rated: duprRated || duprPremium,
        dupr_premium: duprPremium,
        entry_fee_coins: Number(form.entry_fee_coins) || null,
        additional_event_fee: form.additional_event_fee === "" ? null : Number(form.additional_event_fee),
        additional_event_fee_coins: form.additional_event_fee_coins === "" ? null : Number(form.additional_event_fee_coins),
        sponsors,
        early_bird_fee: form.early_bird_fee === "" ? null : Number(form.early_bird_fee),
        early_bird_expires_at: form.early_bird_fee !== "" && form.early_bird_expires_at ? new Date(form.early_bird_expires_at).toISOString() : null,
        vip_addon_fee: form.vip_addon_fee === "" ? null : Number(form.vip_addon_fee),
        vip_addon_fee_coins: form.vip_addon_fee_coins === "" ? null : Number(form.vip_addon_fee_coins),
        price_display: form.price_display,
        max_games: Number(form.max_games) || 0, venue: form.venue,
        playoff_formats: form.playoff_formats,
        capacities, age_brackets: unionAges(), play_style: form.play_style,
        courts, image, video, logo, description: form.description || null,
        prize_pools: tiers.includes("Moneyball")
          ? prizePools.filter((p) => Number(p.players) > 0).map((p) => ({
              players: Number(p.players) || 0, first: Number(p.first) || 0,
              second: Number(p.second) || 0, third: Number(p.third) || 0 }))
          : [],
        team_dupr_caps: tiers.includes("Moneyball")
          ? Object.fromEntries(Object.entries(teamDuprs).filter(([et, v]) => eventTypes.includes(et) && Number(v) > 0).map(([et, v]) => [et, Number(v)]))
          : {},
      };
      const { data } = initial
        ? await api.put(`/tournaments/${initial.id}`, payload)
        : await api.post(endpoint || "/tournaments", payload);
      toast.success(initial ? "Tournament updated!" : "Tournament created!");
      if (!initial) {
        setForm(EMPTY); setEventTypes(["Mixed Doubles"]); setEventCfg({ "Mixed Doubles": { levels: ["All"], age_brackets: [] } }); setMaxEntries(16);
        setCustomAges({}); setCourts([]); setImage(null); setVideo(null);
      }
      onCreated?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="tournament-create-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2"><label className={lbl}>Tournament Name *</label>
          <input data-testid="tcf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bullpen Summer Slam" className={input} /></div>
        <div><label className={lbl}>Format *</label>
          <select data-testid="tcf-format" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className={input}>
            {Object.entries(FORMAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className={lbl}>How the Bracket Gets Played</label>
          <select data-testid="tcf-play-style" value={form.play_style} onChange={(e) => setForm({ ...form, play_style: e.target.value })} className={input}>
            <option value="no_come_around">No Come Around — back draw can't retake the title</option>
            <option value="come_around">Come Around — back-draw winner gets a shot in the final</option>
          </select></div>
        <div><label className={lbl}>Indoor / Outdoor <span className="text-zinc-600 normal-case tracking-normal">(feeds PSI)</span></label>
          <select data-testid="tcf-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className={input}>
            <option value="indoor">Indoor</option>
            <option value="outdoor">Outdoor</option>
          </select></div>
        <div><label className={lbl}>Event Logo <span className="text-zinc-600 normal-case tracking-normal">(shows on rosters, court boards, brackets & emails)</span></label>
          <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={pickLogo} data-testid="tcf-logo-input" />
          {logo ? (
            <div className="flex items-center gap-2">
              <img src={logo} alt="" className="h-10 w-10 object-contain border border-pb-border bg-black" data-testid="tcf-logo-preview" />
              <button data-testid="tcf-logo-remove" type="button" onClick={() => setLogo(null)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button data-testid="tcf-logo-btn" type="button" onClick={() => logoFileRef.current?.click()} disabled={logoUploading}
              className="flex items-center gap-2 border border-pb-border px-3 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-50">
              {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {logoUploading ? "Uploading…" : "Upload Logo"}
            </button>
          )}
        </div>
        <div><label className={lbl}>Announcement Media <span className="text-zinc-600 normal-case tracking-normal">(image or 3-min video)</span></label>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="tcf-image-input" />
          <input ref={videoFileRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" className="hidden" onChange={pickVideo} data-testid="tcf-video-input" />
          <div className="flex mb-1.5">
            {[["image", "Image"], ["video", "Video · 3 min max"]].map(([k, label]) => (
              <button key={k} type="button" data-testid={`tcf-media-tab-${k}`} onClick={() => setMediaTab(k)}
                className={`px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest border transition-colors ${mediaTab === k ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
          {mediaTab === "image" ? (
            image ? (
              <div className="flex items-center gap-2">
                <img src={image.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + image : image} alt="" className="h-10 w-16 object-cover border border-pb-border" data-testid="tcf-image-preview" />
                <button data-testid="tcf-image-remove" onClick={() => setImage(null)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button data-testid="tcf-image-btn" type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="flex items-center gap-2 border border-pb-border px-3 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {uploading ? "Uploading…" : "Upload Image"}
              </button>
            )
          ) : (
            video ? (
              <div className="flex items-center gap-2">
                <video src={video.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + video : video} muted playsInline className="h-10 w-16 object-cover border border-pb-border" data-testid="tcf-video-preview" />
                <button data-testid="tcf-video-remove" onClick={() => setVideo(null)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button data-testid="tcf-video-btn" type="button" onClick={() => videoFileRef.current?.click()} disabled={uploading}
                className="flex items-center gap-2 border border-pb-border px-3 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {uploading ? "Uploading…" : "Upload Video"}
              </button>
            )
          )}</div>
      </div>

      <div>
        <label className={lbl}>Event Types * <span className="text-zinc-600 normal-case tracking-normal">(check all that apply)</span></label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {EVENT_TYPES.filter((et) => !JUNIOR_EVENTS.has(et)).map((et) => (
            <CheckBox key={et} label={et} checked={eventTypes.includes(et)} onToggle={() => toggleEvent(et)}
              testid={`tcf-event-${et.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3 mb-1.5" data-testid="tcf-junior-events-label">Junior Divisions <span className="normal-case tracking-normal text-zinc-600">(Boys / Girls events use the player's profile gender)</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {EVENT_TYPES.filter((et) => JUNIOR_EVENTS.has(et)).map((et) => (
            <CheckBox key={et} label={et} checked={eventTypes.includes(et)} onToggle={() => toggleEvent(et)}
              testid={`tcf-event-${et.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
          ))}
        </div>
      </div>

      {eventTypes.some((et) => et.toLowerCase().includes("doubles")) && (
        <div className="flex items-center justify-between gap-4 border border-pb-border bg-pb-elevated/40 px-4 py-3" data-testid="tcf-partner-toggle-row">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-white">Partner {partnerRequired ? "Required" : "Optional"}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">
              {partnerRequired ? "Players must enter a partner name to register for doubles." : "Players can register for doubles without naming a partner."}
            </div>
          </div>
          <button type="button" data-testid="tcf-partner-toggle" role="switch" aria-checked={partnerRequired}
            onClick={() => setPartnerRequired(!partnerRequired)}
            className={`relative w-12 h-6 rounded-full shrink-0 transition-colors ${partnerRequired ? "bg-pb-red" : "bg-zinc-700"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all duration-200 ${partnerRequired ? "left-[26px]" : "left-0.5"}`} />
          </button>
        </div>
      )}

      <div className="border border-pb-border bg-pb-elevated/40 px-4 py-3 space-y-3" data-testid="tcf-dupr-row">
        {[["dupr", duprRated, (v) => { setDuprRated(v); if (!v) setDuprPremium(false); }, "DUPR-rated event", "Players (and partners) must have a connected DUPR account with BASIC_L1 eligibility to register. Results can be sent to DUPR by the director."],
          ["dupr-premium", duprPremium, (v) => { setDuprPremium(v); if (v) setDuprRated(true); }, "DUPR+ only (PREMIUM_L1)", "Only players with an active DUPR+ membership can register. Implies DUPR-rated."]].map(([k, val, set, label, help]) => (
          <div key={k} className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-white">{label}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">{help}</div>
            </div>
            <button type="button" data-testid={`tcf-${k}-toggle`} role="switch" aria-checked={val} onClick={() => set(!val)}
              className={`relative w-12 h-6 rounded-full shrink-0 transition-colors ${val ? "bg-pb-cyan" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all duration-200 ${val ? "left-[26px]" : "left-0.5"}`} />
            </button>
          </div>
        ))}
      </div>

      <div>
        <label className={lbl}>Max Entries <span className="text-zinc-600 normal-case tracking-normal">(2–256 — applies to every division selected)</span></label>
        <input data-testid="tcf-max-entries" type="number" min={2} max={256} value={maxEntries}
          onChange={(e) => setMaxEntries(e.target.value)}
          className="w-28 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white text-sm focus:outline-none focus:border-pb-red" />
      </div>

      <div className="border border-pb-border bg-pb-elevated/30 p-3" data-testid="tcf-divisions-section">
        <label className={lbl}>Divisions per Event * <span className="text-zinc-600 normal-case tracking-normal">(pick the skill classifications and age groups offered in each event — every combo gets its own draw)</span></label>
        <div className="space-y-3">
          {eventTypes.map((et, idx) => {
            const c = cfgOf(et);
            const junior = JUNIOR_EVENTS.has(et);
            const ageOpts = [...(junior ? JUNIOR_AGE_OPTIONS : AGE_OPTIONS), ...c.age_brackets.filter((a) => !AGE_OPTIONS.includes(a) && !JUNIOR_AGE_OPTIONS.includes(a))];
            const es = slug(et);
            return (
              <div key={et} className="border border-pb-border bg-pb-surface p-3" data-testid={`tcf-evcfg-${es}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className={`text-xs font-display font-bold uppercase tracking-wider ${junior ? "text-emerald-300" : "text-white"}`}>{et}{junior && <span className="ml-2 text-[9px] text-zinc-500 tracking-widest">JUNIOR</span>}</span>
                  {eventTypes.length > 1 && (
                    <button type="button" data-testid={`tcf-evcfg-copy-${es}`} onClick={() => copyCfgToAll(et)}
                      className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-pb-cyan transition-colors">Copy to all events</button>
                  )}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Skill classifications</div>
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {LEVEL_OPTIONS.map((lv) => (
                    <button key={lv} type="button" data-testid={`tcf-evcfg-${es}-level-${slug(lv)}`} aria-pressed={c.levels.includes(lv)} onClick={() => toggleLevel(et, lv)}
                      className={`text-[11px] px-2.5 py-1 border transition-colors ${c.levels.includes(lv) ? "border-pb-red bg-pb-red/15 text-white" : "border-pb-border text-zinc-400 hover:border-zinc-400 hover:text-white"}`}>{lv}</button>
                  ))}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Age groups <span className="normal-case tracking-normal text-zinc-600">(none = open age)</span></div>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {ageOpts.map((a) => (
                    <button key={a} type="button" data-testid={`tcf-evcfg-${es}-age-${slug(a)}`} aria-pressed={c.age_brackets.includes(a)} onClick={() => toggleAge(et, a)}
                      className={`text-[11px] px-2.5 py-1 border transition-colors ${c.age_brackets.includes(a) ? "border-pb-cyan bg-pb-cyan/10 text-pb-cyan" : "border-pb-border text-zinc-400 hover:border-zinc-400 hover:text-white"}`}>{a}</button>
                  ))}
                  <input data-testid={`tcf-evcfg-${es}-age-custom`} value={customAges[et] || ""} onChange={(e) => setCustomAges({ ...customAges, [et]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomAge(et); } }}
                    placeholder={junior ? "Custom (e.g. U8)…" : "Custom (e.g. 19–29)…"} className="bg-pb-elevated border border-pb-border px-2 py-1 text-[11px] text-white w-36 focus:outline-none focus:border-pb-cyan" />
                  <button type="button" data-testid={`tcf-evcfg-${es}-age-custom-add`} onClick={() => addCustomAge(et)}
                    className="border border-pb-border px-2 py-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:border-pb-cyan hover:text-white transition-colors">Add</button>
                </div>
                <div className="text-[10px] text-zinc-500 mt-2" data-testid={`tcf-evcfg-${es}-summary`}>
                  {(c.levels.includes("All") ? 1 : c.levels.length) * Math.max(c.age_brackets.length, 1)} draw{(c.levels.includes("All") ? 1 : c.levels.length) * Math.max(c.age_brackets.length, 1) === 1 ? "" : "s"}: {c.levels.includes("All") ? "Open skill" : c.levels.join(", ")}{c.age_brackets.length ? ` × ${c.age_brackets.join(", ")}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <label className={lbl}>Tournament Tier <span className="text-zinc-600 normal-case tracking-normal">(check all that apply)</span></label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TIER_OPTIONS.map((t) => (
            <CheckBox key={t} label={t} checked={tiers.includes(t)}
              onToggle={() => setTiers(tiers.includes(t) ? tiers.filter((x) => x !== t) : [...tiers, t])}
              testid={`tcf-tier-${t.toLowerCase()}`} />
          ))}
        </div>
      </div>

      {tiers.includes("Moneyball") && (
        <div className="border border-yellow-500/30 bg-yellow-500/[0.03] p-3 space-y-3" data-testid="tcf-moneyball-purse-section">
          <div>
            <label className={`${lbl} !text-yellow-500`}>Combined Team DUPR <span className="text-zinc-600 normal-case tracking-normal">(per event — both partners' DUPR added together, PSI auto-calculated)</span></label>
            <div className="space-y-2">
              {eventTypes.map((et) => {
                const psi = teamDuprToPsi(teamDuprs[et]);
                const slug = et.toLowerCase().replace(/[^a-z]+/g, "-");
                return (
                  <div key={et} className="grid grid-cols-2 sm:grid-cols-3 gap-2 items-center border border-pb-border px-3 py-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold truncate">{et}</span>
                    <input data-testid={`tcf-team-dupr-${slug}`} type="number" min={4} max={16} step={0.1} placeholder="e.g. 7.5"
                      value={teamDuprs[et] ?? ""} onChange={(e) => setTeamDuprs({ ...teamDuprs, [et]: e.target.value })} className={input} />
                    <div data-testid={`tcf-team-psi-${slug}`} className={`text-sm font-bold ${psi ? "text-yellow-400" : "text-zinc-600"}`}>
                      {psi ? `${psi} Team PSI` : "— Team PSI"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className={`${lbl} !mb-0 !text-yellow-500`}>Moneyball Prize Pool <span className="text-zinc-600 normal-case tracking-normal">(up to 3 purse structures by player count)</span></label>
            {prizePools.length < 3 && (
              <button type="button" data-testid="tcf-purse-add"
                onClick={() => setPrizePools([...prizePools, { players: "", first: "", second: "", third: "" }])}
                className="border border-yellow-500/40 text-yellow-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-yellow-500/10 transition-colors">
                + Add Purse Structure
              </button>
            )}
          </div>
          {prizePools.length === 0 && (
            <p className="text-xs text-zinc-500">Add a purse structure — set the player count, then the 1st, 2nd and 3rd place purse amounts.</p>
          )}
          {prizePools.map((pp, i) => (
            <div key={i} className="border border-pb-border p-3" data-testid={`tcf-purse-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-yellow-500">Purse Structure {i + 1}</span>
                <button type="button" data-testid={`tcf-purse-${i}-remove`}
                  onClick={() => setPrizePools(prizePools.filter((_, x) => x !== i))}
                  className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div><label className={lbl}># of Players</label>
                  <input data-testid={`tcf-purse-${i}-players`} type="number" min={0} placeholder="e.g. 16" value={pp.players}
                    onChange={(e) => setPrizePools(prizePools.map((p, x) => x === i ? { ...p, players: e.target.value } : p))} className={input} /></div>
                <div><label className={lbl}>1st Place ($)</label>
                  <input data-testid={`tcf-purse-${i}-first`} type="number" min={0} placeholder="0" value={pp.first}
                    onChange={(e) => setPrizePools(prizePools.map((p, x) => x === i ? { ...p, first: e.target.value } : p))} className={input} /></div>
                <div><label className={lbl}>2nd Place ($)</label>
                  <input data-testid={`tcf-purse-${i}-second`} type="number" min={0} placeholder="0" value={pp.second}
                    onChange={(e) => setPrizePools(prizePools.map((p, x) => x === i ? { ...p, second: e.target.value } : p))} className={input} /></div>
                <div><label className={lbl}>3rd Place ($)</label>
                  <input data-testid={`tcf-purse-${i}-third`} type="number" min={0} placeholder="0" value={pp.third}
                    onChange={(e) => setPrizePools(prizePools.map((p, x) => x === i ? { ...p, third: e.target.value } : p))} className={input} /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <label className={lbl}>Court Numbers * <span className="text-zinc-600 normal-case tracking-normal">(check all courts in play — used for live court assignments)</span></label>
        <div className="grid grid-cols-8 sm:grid-cols-12 gap-1.5">
          {Array.from({ length: 24 }, (_, i) => i + 1).map((c) => (
            <button key={c} type="button" data-testid={`tcf-court-${c}`} aria-pressed={courts.includes(c)} onClick={() => toggleCourt(c)}
              className={`h-8 text-xs font-bold border transition-colors ${courts.includes(c) ? "border-green-500 bg-green-500/15 text-green-400" : "border-pb-border text-zinc-600 hover:border-zinc-500"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div><label className={lbl}>Start Date *</label>
          <input data-testid="tcf-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Finish Date</label>
          <input data-testid="tcf-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Days <span className="text-zinc-600 normal-case tracking-normal">(1-7)</span></label>
          <input data-testid="tcf-days" type="number" min={1} max={7} value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Start Time</label>
          <input data-testid="tcf-start-time" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Finish Time</label>
          <input data-testid="tcf-end-time" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={input} /></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Registration Opens <span className="text-zinc-600 normal-case tracking-normal">(optional — card shows "Opening Soon" until then)</span></label>
          <input data-testid="tcf-reg-open" type="datetime-local" value={form.reg_open_at} onChange={(e) => setForm({ ...form, reg_open_at: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Registration Closes <span className="text-zinc-600 normal-case tracking-normal">(optional — auto-closes at this time)</span></label>
          <input data-testid="tcf-reg-close" type="datetime-local" value={form.reg_close_at} onChange={(e) => setForm({ ...form, reg_close_at: e.target.value })} className={input} /></div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className={lbl}>Location *</label>
          <input data-testid="tcf-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Bullpen Courts" className={input} /></div>
        <div><label className={lbl}>Zip Code <span className="text-zinc-600 normal-case tracking-normal">(targets local player alerts)</span></label>
          <input data-testid="tcf-zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="34205" maxLength={10} className={input} /></div>
        <div><label className={lbl}>Entry Fee ($, 0 = free)</label>
          <input data-testid="tcf-fee" type="number" min={0} value={form.entry_fee} onChange={(e) => setForm({ ...form, entry_fee: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Coin Fee (🪙 · blank = no coin pay)</label>
          <input data-testid="tcf-fee-coins" type="number" min={0} value={form.entry_fee_coins} onChange={(e) => setForm({ ...form, entry_fee_coins: e.target.value })} className={input} /></div>
        <div><label className={lbl}>2nd Event Fee ($ · blank = full price)</label>
          <input data-testid="tcf-additional-fee" type="number" min={0} value={form.additional_event_fee} onChange={(e) => setForm({ ...form, additional_event_fee: e.target.value })} className={input} />
          <p className="text-[10px] text-zinc-600 mt-1">Charged instead of the entry fee when a player adds another event.</p></div>
        <div><label className={lbl}>2nd Event Coin Fee (🪙 · blank = full price)</label>
          <input data-testid="tcf-additional-fee-coins" type="number" min={0} value={form.additional_event_fee_coins} onChange={(e) => setForm({ ...form, additional_event_fee_coins: e.target.value })} className={input} /></div>
        <div><label className={lbl}>Guaranteed Games Per Team <span className="text-zinc-600 normal-case tracking-normal">(0 = full round robin — every team plays at least this many)</span></label>
          <input data-testid="tcf-max-games" type="number" min={0} max={20} value={form.max_games} onChange={(e) => setForm({ ...form, max_games: e.target.value })} className={input} />
          <p className="text-[10px] text-zinc-600 mt-1">Round robin: each team plays up to this many games, then playoffs begin.</p></div>
      </div>

      <SponsorsEditor sponsors={sponsors} onChange={setSponsors} input={input} lbl={lbl} />

      <div className="border border-emerald-500/30 bg-emerald-500/[0.03] p-3" data-testid="tcf-pricing-section">
        <div className={`${lbl} !text-emerald-400`}>Pricing Options <span className="text-zinc-600 normal-case tracking-normal">(Early Bird auto-expires · VIP is an optional add-on players pick at signup)</span></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><label className={lbl}>Early Bird Price ($ · blank = none)</label>
            <input data-testid="tcf-early-bird-fee" type="number" min={0} value={form.early_bird_fee} onChange={(e) => setForm({ ...form, early_bird_fee: e.target.value })} className={input} />
            <p className="text-[10px] text-zinc-600 mt-1">Replaces the entry fee for a player's first event until it expires.</p></div>
          <div><label className={lbl}>Early Bird Expires {form.early_bird_fee !== "" && "*"}</label>
            <input data-testid="tcf-early-bird-expires" type="datetime-local" value={form.early_bird_expires_at} onChange={(e) => setForm({ ...form, early_bird_expires_at: e.target.value })} className={input} />
            <p className="text-[10px] text-zinc-600 mt-1">Registration switches to the regular fee automatically after this.</p></div>
          <div><label className={lbl}>Card Shows Price In</label>
            <select data-testid="tcf-price-display" value={form.price_display} onChange={(e) => setForm({ ...form, price_display: e.target.value })} className={input}>
              <option value="card">Card · USD ($)</option>
              <option value="coin">Coin · Bullion (BLC)</option>
            </select>
            <p className="text-[10px] text-zinc-600 mt-1">Headline price on the tournament card. Both payment options stay available at checkout.</p></div>
          <div><label className={lbl}>VIP Section Add-on ($ · blank = none)</label>
            <input data-testid="tcf-vip-fee" type="number" min={0} value={form.vip_addon_fee} onChange={(e) => setForm({ ...form, vip_addon_fee: e.target.value })} className={input} />
            <p className="text-[10px] text-zinc-600 mt-1">Optional upgrade added to the player's total; VIP entries are flagged on your roster.</p></div>
          <div><label className={lbl}>VIP Section Coin Price (🪙 · blank = card only)</label>
            <input data-testid="tcf-vip-fee-coins" type="number" min={0} value={form.vip_addon_fee_coins} onChange={(e) => setForm({ ...form, vip_addon_fee_coins: e.target.value })} className={input} /></div>
        </div>
      </div>

      <div>
        <label className={lbl}>Playoff Format <span className="text-zinc-600 normal-case tracking-normal">(quarters / semis / finals — adjustable again right before you start playoffs)</span></label>
        <div className="grid grid-cols-3 gap-3">
          {[["quarters", "Quarterfinals", [1, 3]], ["semis", "Semifinals", [1, 3]], ["finals", "Finals", [1, 3, 5]]].map(([k, label, opts]) => (
            <div key={k}>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">{label}</div>
              <select data-testid={`tcf-po-${k}`} value={form.playoff_formats?.[k] || 1}
                onChange={(e) => setForm({ ...form, playoff_formats: { ...form.playoff_formats, [k]: Number(e.target.value) } })}
                className={input}>
                {opts.map((n) => <option key={n} value={n}>{n === 1 ? "Single game" : `Best of ${n}`}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      <div><label className={lbl}>Description</label>
        <textarea data-testid="tcf-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Rules, prizes, divisions…" className={`${input} resize-none`} /></div>

      <button data-testid="tcf-submit" onClick={submit} disabled={busy || uploading}
        className="w-full sm:w-auto bg-pb-red text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
        {busy ? (initial ? "Saving…" : "Creating…") : (initial ? "Save Changes" : "Create Tournament")}
      </button>
    </div>
  );
}

